import {
    GET_LIST,
    GET_ONE,
} from 'react-admin';
import queryString from 'query-string';
import logger from '../utils/logger';
import { APPOINTMENT_TYPES_RESOURCE_NAME } from './appointment-types.model';

// Healthie endpoints are V2-only (no V1 equivalent), so always use V2 API URL
const getApiUrl = () => import.meta.env.VITE_API_V2_URL;

/**
 * Convert React Admin request to HTTP request for Healthie proxy endpoints
 * 
 * NOTE: The Healthie proxy endpoints use a different response format:
 * { success: true, data: [...], pagination: {...}, meta: {...} }
 * 
 * This differs from standard Admin Panel endpoints which use Content-Range headers.
 */
const providerRequestToHttpRequest = (requestType, requestParams) => {
    switch (requestType) {
        case GET_LIST: {
            const { page, perPage } = requestParams.pagination;
            const { field, order } = requestParams.sort;
            const filters = requestParams.filter || {};

            // Build query parameters for Healthie proxy endpoint
            const query = {
                page: page,
                page_size: perPage,
            };

            // Add optional filters
            if (filters.clients_can_book !== undefined) {
                query.clients_can_book = filters.clients_can_book;
            }
            if (filters.is_group !== undefined) {
                query.is_group = filters.is_group;
            }
            if (filters.active_only !== undefined) {
                query.active_only = filters.active_only;
            } else {
                // Default to showing only active (non-deleted) appointment types
                query.active_only = true;
            }

            // Add sorting
            if (field && order) {
                query.sort = order === 'DESC' ? `-${field}` : field;
            }

            const url = `${getApiUrl()}/healthie/appointment-types?${queryString.stringify(query)}`;
            
            logger.log('[AppointmentTypes DataProvider] GET_LIST request:', {
                page,
                perPage,
                filters,
                url,
            });

            return { url };
        }

        case GET_ONE: {
            const url = `${getApiUrl()}/healthie/appointment-types/${requestParams.id}`;
            
            logger.log('[AppointmentTypes DataProvider] GET_ONE request:', {
                id: requestParams.id,
                url,
            });

            return { url };
        }

        default:
            throw new Error(`Unsupported fetch action type ${requestType}`);
    }
};

/**
 * Convert HTTP response to React Admin data format
 * 
 * Handles the Healthie proxy endpoint response format:
 * { success: true, data: [...], pagination: {...}, meta: {...} }
 */
const httpResponseToProviderData = (httpResponse, requestType, requestParams) => {
    const { json } = httpResponse;

    switch (requestType) {
        case GET_LIST: {
            // Handle Healthie proxy response format
            if (json.success && json.data && json.pagination) {
                logger.log('[AppointmentTypes DataProvider] GET_LIST response:', {
                    count: json.data.length,
                    total: json.pagination.total,
                    page: json.pagination.page,
                    cached: json.meta?.cached,
                });

                return {
                    data: json.data,
                    total: json.pagination.total,
                };
            }

            // Fallback for unexpected format
            logger.warn('[AppointmentTypes DataProvider] Unexpected response format:', json);
            return {
                data: Array.isArray(json) ? json : [],
                total: Array.isArray(json) ? json.length : 0,
            };
        }

        case GET_ONE: {
            // Handle Healthie proxy response format
            if (json.success && json.data) {
                logger.log('[AppointmentTypes DataProvider] GET_ONE response:', {
                    id: json.data.id,
                    name: json.data.name,
                    cached: json.meta?.cached,
                });

                return { data: json.data };
            }

            // Fallback for unexpected format
            logger.warn('[AppointmentTypes DataProvider] Unexpected response format:', json);
            return { data: json };
        }

        default:
            return { data: json };
    }
};

export default {
    resource: APPOINTMENT_TYPES_RESOURCE_NAME,
    providerInterface: {
        providerRequestToHttpRequest,
        httpResponseToProviderData,
    },
};

